import { useEffect, useMemo, useRef } from 'react';
import { Title, Box, Spinner } from '@nimbus-ds/components';
import { useParams } from 'react-router-dom';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import { useNavegate } from 'App/hooks';
import { IonPageStratus, HeaderTop } from 'commons/components';
import { CheckoutProvider } from 'domains/Billing/Checkout/CheckoutUI';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { DomainsCheckout } from './components';
import { useGetDomainOrderQuery } from '../../domainsApi';
import { trackingNuvemDomainsCheckout } from '../../tracking';
import './DomainsCheckoutPage.scss';

interface DomainsCheckoutPageParams {
  payOrderId: string;
}

export function DomainsCheckoutPage() {
  const t = useTranslationConfigurations('domains.checkout');
  const { hideMenu, showMenu } = useDesktopMenuContext();
  const { payOrderId } = useParams<DomainsCheckoutPageParams>();
  const { goBack } = useNavegate();
  const hasTrackedCheckout = useRef(false);

  const { data: orderData, isLoading } = useGetDomainOrderQuery(
    payOrderId ?? '',
    {
      skip: !payOrderId,
    },
  );

  const backNavigation = useMemo(
    () => ({
      onClick: goBack,
    }),
    [goBack],
  );

  useEffect(() => {
    hideMenu();

    return () => {
      showMenu();
    };
  }, [hideMenu, showMenu]);

  useEffect(() => {
    if (!isLoading && orderData && payOrderId && !hasTrackedCheckout.current) {
      trackingNuvemDomainsCheckout();
      hasTrackedCheckout.current = true;
    }
  }, [isLoading, orderData, payOrderId]);

  return (
    <IonPageStratus
      width="medium"
      headerTop={<HeaderTop navigation={backNavigation} />}
    >
      {isLoading || !orderData || !payOrderId ? (
        <Box padding="4" display="flex" justifyContent="center">
          <Spinner />
        </Box>
      ) : (
        <Box padding="4">
          <Title>
            {t('pageTitle')} {orderData?.domainName}
          </Title>
          <CheckoutProvider payOrderId={payOrderId}>
            <DomainsCheckout order={orderData} />
          </CheckoutProvider>
        </Box>
      )}
    </IonPageStratus>
  );
}
